import { ComponentPropsWithoutRef, forwardRef } from "react";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import styles from "./Drawer.module.scss";
import { Text } from "../Text";
import { DrawerCancelButton } from "./DrawerCancelButton";
import cx from "classnames";
import { DataTrackingId } from "../../types";
import { childrenToString } from "../../internal/functions";
import { useTrackingId, useMergeRefs } from "../../hooks";
import { useDrawerContext } from "./internal/useDrawerContext";

/**
 * Props for the DrawerHeader component
 * @extends ComponentPropsWithoutRef<"header">
 */
export type DrawerHeaderProps = ComponentPropsWithoutRef<"header"> &
  DataTrackingId;

/**
 * Header component for the drawer that displays a title and close button.
 *
 * Features:
 * - Displays the drawer title as a headline
 * - Includes an accessible close button
 * - Sticky positioning at the top of the drawer
 * - Proper semantic HTML structure
 * - Accessible with proper ARIA attributes
 * - Consistent styling with the design system
 * - Automatic close functionality
 *
 * @example
 * <Drawer.Header>Settings</Drawer.Header>
 */
export const DrawerHeader = forwardRef<HTMLHeadElement, DrawerHeaderProps>(
  (props, ref) => {
    const { className, children } = props;
    const { headerRef } = useDrawerContext();
    const data = {
      children: childrenToString(props.children),
    };

    const trackingId = useTrackingId({
      name: "DrawerHeader",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const drawerHeaderClasses = cx(styles.header, className);

    return (
      <header
        className={drawerHeaderClasses}
        data-anv="drawer-header"
        data-tracking-id={trackingId}
        ref={useMergeRefs([ref, headerRef])}
      >
        <Text variant="headline" el="h1">
          {children}
        </Text>
        <span className={styles["close-container"]}>
          <DrawerCancelButton
            className={styles["close-button"]}
            size="small"
            appearance="ghost"
            aria-label="Close drawer"
            data-anv="drawer-header-close-button"
            icon={Close}
          />
        </span>
      </header>
    );
  },
);

DrawerHeader.displayName = "DrawerHeader";
